import { useEffect, useState } from 'react';
import { faPlus, faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useNavigate } from 'react-router-dom';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationTotalStandalone,
  PaginationProvider,
  PaginationListStandalone
} from 'react-bootstrap-table2-paginator';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Dropdown, Alert, Spinner } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import FilterTab from 'components/common/FilterTab';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import useAdvanceTable from 'hooks/useAdvanceTable';
import FeatherIcon from 'feather-icons-react';
import { CustomSortCaret } from 'helpers/ui-utils';
import { getClients, getClientsBySearchQuery } from 'api/api';
import SearchBox from 'components/common/SearchBox';

const Clients = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [error, setError] = useState([]);
  const [total, setTotal] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQ, setSearchQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState('asc');
  const [columnName, setColumnName] = useState('id');
  const [sortString, setSortString] = useState('id:desc');

  useEffect(() => {
    loadData(currentPage, pageSize, 'id:desc');
  }, []);

  const loadData = async (pageNr, pageSize, sortBy) => {
    setError([]);
    setLoading(true);

    await getClients(pageNr, pageSize, sortBy, searchQ)
      .then(response => {
        setData(response.data.data.clients);
        setTotal(response.data.data.total);
      })
      .catch(err => {
        setLoading(false);
        try {
          if (err.response.status === 401) {
            navigate('/sign-in');
          }
          setError(err.response.data.errors);
        } catch (ex) {
          setError(['Please try again later...']);
        }
      });

    setLoading(false);
  };

  const searchData = async (pageNr, pageSize, sortBy, searchText) => {
    setError([]);
    setLoading(true);

    if (!searchText || searchText.trim() === '') {
      setError(['Enter text to search']);
    }

    await getClientsBySearchQuery(pageNr, pageSize, sortBy, searchText)
      .then(response => {
        setData(response.data.data.clients);
        setTotal(response.data.data.total);
      })
      .catch(err => {
        setLoading(false);
        try {
          if (err.response.status === 401) {
            navigate('/sign-in');
          }
          setError(err.response.data.errors);
        } catch (ex) {
          setError(['Please try again later...']);
        }
      });

    setLoading(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const CustomSortCaretUpdatedByOrder = (order, dataField) => {
    if (columnName === 'id')
      return (
        <span className="custom-sort-cart">
          <div style={{ display: 'inline-block' }}>
            ▲
            <br />▼
          </div>
        </span>
      );
    else if (sortColumn === 'asc')
      return (
        <span className="custom-sort-cart">
          <div style={{ display: 'inline-block' }}>
            <div style={{ display: 'inline-block' }}> ▲ </div>
            <br />
            <div style={{ display: 'inline-block', visibility: 'collapse' }}>
              ▼
            </div>
          </div>
        </span>
      );
    else if (sortColumn === 'desc')
      return <span className="custom-sort-cart">▼</span>;
    return null;
  };

  const columns = [
    {
      dataField: 'name',
      text: 'NAME',
      headerStyle: { width: '15%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'name' ? CustomSortCaretUpdatedByOrder : CustomSortCaret,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        color: 'phoenix-gray-1100'
      }
    },
    {
      dataField: 'contact_name',
      text: 'CONTACT NAME',
      headerStyle: { width: '15%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'contact_name'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        color: 'phoenix-gray-1100'
      }
    },
    {
      dataField: 'address',
      text: 'ADDRESS',
      headerStyle: { width: '15%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'address'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100'
      }
    },

    {
      dataField: 'city',
      text: 'CITY',
      headerStyle: { width: '15%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'city' ? CustomSortCaretUpdatedByOrder : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100'
      }
    },
    {
      dataField: 'state',
      text: 'State',
      headerStyle: { width: '15%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'state'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100'
      }
    },
    {
      dataField: 'zip',
      text: 'ZIP',
      headerStyle: { width: '10%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'zip' ? CustomSortCaretUpdatedByOrder : CustomSortCaret,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        color: 'phoenix-gray-1100'
      }
    },
    // {
    //   dataField: 'email',
    //   text: 'Email',
    //   headerStyle: { width: '15%', fontSize: '13px' },
    //   sort:false,
    //   sortCaret:
    //     columnName === 'email'
    //       ? CustomSortCaretUpdatedByOrder
    //       : CustomSortCaret,
    //   style: {
    //     fontSize: '12px',
    //     color: 'phoenix-gray-1100'
    //   }
    // },

    {
      dataField: 'phone',
      text: 'PHONE',
      headerStyle: { width: '15%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'phone'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100'
      }
    },
    {
      dataField: 'status',
      text: 'STATUS',
      sort: false,
      sortCaret:
        columnName === 'status'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      headerStyle: { width: '15%', fontSize: '13px' },
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100'
      },
      formatter: (cell, row) => {
        if (row.status.name === 'Active') {
          return (
            <Badge
              bg={'success'}
              variant="phoenix"
              iconPosition="end"
              className="fs-10"
              icon={<FeatherIcon icon={'check'} size={12} className="ms-1" />}
            >
              {row.status.name}
            </Badge>
          );
        } else {
          return (
            <Badge
              bg={'danger'}
              variant="phoenix"
              iconPosition="end"
              className="fs-10"
              icon={<FeatherIcon icon={'info'} size={12} className="ms-1" />}
            >
              {row.status.name}
            </Badge>
          );
        }
      }
    },

    {
      dataField: 'actions',
      formatter: (cell, row) => (
        <div className="table-action-button">
          <RevealDropdownTrigger>
            <RevealDropdown onSelect={handleSelect}>
              <Dropdown.Item eventKey={`edit:${row.id}`}>Edit</Dropdown.Item>
              <Dropdown.Item
                key={`${row.id}`}
                className="text-danger"
                eventKey={`delete:${row.id}`}
              >
                Delete
              </Dropdown.Item>
            </RevealDropdown>
          </RevealDropdownTrigger>
        </div>
      )
    }
  ];

  const handleSelect = eventKey => {
    // Handle the selection here
    const parts = eventKey.split(':');
    if (parts[0] === 'edit') {
      navigate(`/clients/edit-client/${parts[1]}`);
    } else if (parts[0] === 'delete') {
      navigate(`/clients/delete-client/${parts[1]}`);
    } else if (parts[0] === 'log') {
      navigate(`/users/user-log/${parts[1]}`);
    } else if (parts[0] === 'password') {
      navigate(`/users/change-password/${parts[1]}`);
    } else if (parts[0] === 'pin') {
      navigate(`/users/change-pin/${parts[1]}`);
    }
  };

  const handleAllClick = () => {
    setCurrentPage(1);
    loadData(1, 10, 'id:desc');
  };

  const tabItems = [
    {
      label: 'All',
      value: 'all',
      count: total,
      onClick: handleAllClick
    }
  ];

  const table = useAdvanceTable({
    data: data
  });

  const handleSearchInputChange = e => {
    setSearchQ(e.target.value);
    if (e.target.value.length > 1) searchData(1, 10, 'id:desc', e.target.value);
    else if (e.target.value.length === 0) loadData(1, 10, 'id:desc');
  };

  const handleSearchPaste = e => {
    setTimeout(() => {
      const pastedText = e.target.value;
      setSearchQ(pastedText);

      if (pastedText.length > 1) searchData(1, 10, 'id:desc', pastedText);
      else if (pastedText.length === 0) loadData(1, 10, 'id:desc');
    }, 0);
  };

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder, data }
  ) => {
    switch (type) {
      case 'pagination':
        pagination(page, sizePerPage);
        break;
      case 'sort':
        sort(sortField, sortOrder, data);
        break;
      default:
        break;
    }
  };

  const pagination = (page, sizePerPage) => {
    setCurrentPage(page);
    setPageSize(sizePerPage);
    loadData(page, sizePerPage, sortString);
  };

  const sort = sortField => {
    setCurrentPage(1);
    if (sortColumn === 'desc') {
      setSortColumn('asc');
    } else if (sortColumn === 'asc') {
      setSortColumn('desc');
    }
    switch (sortField) {
      case 'first_name':
        setColumnName('first_name');
        setSortString(`first_name:${sortColumn}`);
        loadData(1, 10, `first_name:${sortColumn}`);
        break;
      case 'last_name':
        setColumnName('last_name');
        setSortString(`last_name:${sortColumn}`);
        loadData(1, 10, `last_name:${sortColumn}`);
        break;
      case 'group':
        setColumnName('group');
        setSortString(`user_group.name:${sortColumn}`);
        loadData(1, 10, `user_group.name:${sortColumn}`);
        break;
      case 'status':
        setColumnName('status');
        setSortString(`status.name:${sortColumn}`);
        loadData(1, 10, `status.name:${sortColumn}`);
        break;
      default:
        break;
    }
  };

  return (
    <div>
      <div className="mb-9">
        <h2 className="mb-4">Clients</h2>
        <FilterTab tabItems={tabItems} className="mb-2" />

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Search Client"
                onChange={handleSearchInputChange}
                onPaste={handleSearchPaste}
              />
              <div className="scrollbar overflow-hidden-y"></div>
              <div className="ms-xxl-auto">
                {/* Bulk Print Button */}
                <Button
                  variant="primary"
                  className="me-2"
                  onClick={() => navigate(`/clients/bulk-print/`, {})}
                >
                  <FontAwesomeIcon icon={faPrint} className="me-2" />
                  Bulk Print
                </Button>

                {/* Add Clients in Bulk Button */}
                <Button
                  variant="primary"
                  className="me-2"
                  onClick={() =>
                    navigate(`/clients/add-client-bulk/`, {
                      state: { customState: 'stockItem' }
                    })
                  }
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Clients - Bulk
                </Button>

                {/* Add Single Client Button */}
                <Button
                  variant="primary"
                  onClick={() => navigate(`/clients/add-client/`)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Client
                </Button>
              </div>
            </div>
          </div>

          <div>
            {Object.entries(error).length > 0 &&
              error.map((e, index) => (
                <Alert variant="soft-danger" key={index}>
                  {e}
                </Alert>
              ))}
          </div>

          <div className="bg-white border-top border-bottom border-200 position-relative top-1">
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: '200px' }}
              >
                <Spinner animation="border" role="status"></Spinner>
              </div>
            ) : (
              <PaginationProvider
                pagination={paginationFactory({
                  custom: true,
                  page: currentPage,
                  sizePerPage: pageSize,
                  totalSize: total,
                  sizePerPageList: [
                    {
                      text: '10',
                      value: 10
                    }
                  ]
                })}
              >
                {({ paginationProps, paginationTableProps }) => (
                  <div>
                    <BootstrapTable
                      remote
                      keyField="id"
                      data={data.length === 0 ? [] : data}
                      columns={columns}
                      bordered={false}
                      onTableChange={handleTableChange}
                      {...paginationTableProps}
                    />
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: '-10px',
                          fontSize: '13px'
                        }}
                      >
                        <PaginationTotalStandalone {...paginationProps} />
                        <PaginationListStandalone {...paginationProps} />
                      </div>
                    </div>
                  </div>
                )}
              </PaginationProvider>
            )}
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Clients;
